/**
 * bridgeClient — iter-235
 *
 * Tiny WebSocket client that publishes ELPM Studio's transport events
 * (play / pause / stop / record / bpm) to the Bridge backend so the
 * Node desktop bridge can translate them into MIDI MMC + Clock for the
 * user's native DAW.
 *
 * iter-240 (Phase 2) added inbound transport — when the user enables
 * Reverse Sync in the bridge popover AND the desktop bridge has a
 * `--input-port` configured, MMC/start/stop messages flow back into
 * ELPM via the `transportListeners` set. Events are tagged with
 * `source:"daw"` so we don't bounce them right back out as MIDI.
 *
 * State model is intentionally simple:
 *   - sessionCode + autoConnect persisted to localStorage
 *   - WebSocket lifecycle managed inside a singleton (one bridge per tab)
 *   - exposes broadcast(event) so the engine's transport handlers can call
 *     it without knowing anything about WebSockets.
 *
 * Backend contract:
 *   POST /api/bridge/session             → { session_code }
 *   WS   /api/bridge/ws/{session_code}   → bidirectional JSON relay
 */

const STORAGE_CODE = "elpm-bridge-session-code";
const STORAGE_AUTO = "elpm-bridge-autoconnect";
// iter-236 — Latency offset (ms). Delays ELPM's own audio start by N ms
// while the bridge fires MMC immediately, so Ableton's natural buffer
// latency catches up and the two transports lock. Range 0-300 ms.
const STORAGE_LATENCY = "elpm-bridge-latency-ms";
// iter-240 — Reverse Sync (DAW → ELPM). When enabled, transport events
// inbound from the bridge actually drive ELPM's engine instead of being
// ignored. Off by default to keep iter-235 behaviour unchanged.
const STORAGE_REVERSE = "elpm-bridge-reverse-sync";
// iter-243 — MIDI Sync (Phase 3 extended MIDI). Separate from Reverse
// Sync because a user may want their DAW's mod wheel / keyboard to drive
// ELPM faders/instruments WITHOUT also surrendering transport control,
// or vice versa.
const STORAGE_MIDI = "elpm-bridge-midi-sync";

let ws = null;
let currentCode = null;
let listeners = new Set();
// iter-240 — separate fan-out for transport events received from the
// bridge. Subscribers get { event, bpm?, at, source } payloads.
let transportListeners = new Set();
// iter-258 — Fan-out for the bridge's `hello` handshake. Subscribers
// get { version, at } whenever the peer (desktop bridge) advertises
// itself. Used by BridgeBadge to drive the "Update available" pill.
let bridgeVersionListeners = new Set();
let lastBridgeVersion = null;
// iter-259 — Last full hello payload (version + ports + platform) for
// the Bridge Health dashboard. Distinct from `lastBridgeVersion` so
// callers that only care about the version string don't have to re-
// process the wider object. Cleared on disconnect alongside the
// version snapshot.
let lastBridgeHello = null;
let bridgeHelloListeners = new Set();
// iter-259 — Pong fan-out for the ping/pong heartbeat. Subscribers
// get { id, rtt_ms, ack_at } so they can build the RTT history graph
// in the health dashboard. The id we sent on ping is echoed by the
// bridge so multiple in-flight pings can be disambiguated.
let bridgePongListeners = new Set();

function notify() {
  for (const fn of listeners) {
    try { fn(getStatus()); } catch (e) { void e; }
  }
}

export function getStatus() {
  return {
    code: currentCode,
    state: ws?.readyState === 1 ? "open" : ws?.readyState === 0 ? "connecting" : "closed",
  };
}

export function subscribe(fn) {
  listeners.add(fn);
  fn(getStatus());
  return () => listeners.delete(fn);
}

export function getSavedCode() {
  try { return localStorage.getItem(STORAGE_CODE) || ""; } catch (e) { void e; return ""; }
}

export function getAutoConnect() {
  try { return localStorage.getItem(STORAGE_AUTO) === "1"; } catch (e) { void e; return false; }
}

export function setAutoConnect(v) {
  try { localStorage.setItem(STORAGE_AUTO, v ? "1" : "0"); } catch (e) { void e; }
}

export function getLatencyOffset() {
  try {
    const v = parseInt(localStorage.getItem(STORAGE_LATENCY) || "0", 10);
    return Number.isFinite(v) ? Math.max(0, Math.min(300, v)) : 0;
  } catch (e) { void e; return 0; }
}

export function setLatencyOffset(ms) {
  const clean = Math.max(0, Math.min(300, parseInt(ms, 10) || 0));
  try { localStorage.setItem(STORAGE_LATENCY, String(clean)); } catch (e) { void e; }
  return clean;
}

// iter-240 — Reverse Sync persistence + subscription API.
export function getReverseSync() {
  try { return localStorage.getItem(STORAGE_REVERSE) === "1"; } catch (e) { void e; return false; }
}

export function setReverseSync(v) {
  try { localStorage.setItem(STORAGE_REVERSE, v ? "1" : "0"); } catch (e) { void e; }
}

// iter-243 — MIDI Sync persistence (CC + Note in/out).
export function getMidiSync() {
  try { return localStorage.getItem(STORAGE_MIDI) === "1"; } catch (e) { void e; return false; }
}

export function setMidiSync(v) {
  try { localStorage.setItem(STORAGE_MIDI, v ? "1" : "0"); } catch (e) { void e; }
}

/** Subscribe to transport events received FROM the bridge (DAW → ELPM).
 *  The callback receives `{ event, bpm?, at, source }`. Returns an
 *  unsubscribe function. Honour `getReverseSync()` inside your callback —
 *  events are always fanned out; whether to act on them is the consumer's
 *  call. */
export function onTransport(fn) {
  transportListeners.add(fn);
  return () => transportListeners.delete(fn);
}

function fanoutTransport(msg) {
  for (const fn of transportListeners) {
    try { fn(msg); } catch (e) { void e; }
  }
}

// iter-258 — Subscribe to the connected bridge's version advertisement.
// Callback receives `{ version, at }`. Fires immediately on subscribe
// if we've already seen a hello in this WS session.
export function onBridgeVersion(fn) {
  bridgeVersionListeners.add(fn);
  if (lastBridgeVersion != null) {
    try { fn({ version: lastBridgeVersion, at: 0, replayed: true }); } catch (e) { void e; }
  }
  return () => bridgeVersionListeners.delete(fn);
}

function fanoutBridgeVersion(msg) {
  lastBridgeVersion = msg.version || null;
  for (const fn of bridgeVersionListeners) {
    try { fn(msg); } catch (e) { void e; }
  }
}

// iter-259 — Subscribe to the full bridge hello payload (version,
// midi_out_port, midi_in_port, platform). Replays the last seen
// payload to late subscribers via { replayed: true }.
export function onBridgeHello(fn) {
  bridgeHelloListeners.add(fn);
  if (lastBridgeHello) {
    try { fn({ ...lastBridgeHello, replayed: true }); } catch (e) { void e; }
  }
  return () => bridgeHelloListeners.delete(fn);
}

function fanoutBridgeHello(msg) {
  lastBridgeHello = msg;
  for (const fn of bridgeHelloListeners) {
    try { fn(msg); } catch (e) { void e; }
  }
}

// iter-259 — Subscribe to pong replies. Receives `{ id, rtt_ms,
// ack_at }`. Returns an unsubscribe function.
export function onBridgePong(fn) {
  bridgePongListeners.add(fn);
  return () => bridgePongListeners.delete(fn);
}

function fanoutBridgePong(msg) {
  for (const fn of bridgePongListeners) {
    try { fn(msg); } catch (e) { void e; }
  }
}

// iter-259 — Send a ping to the bridge. Returns the generated id so
// the caller can match it against the next pong if they want. Pings
// while disconnected are silently dropped (return null).
let _pingCounter = 0;
export function sendPing() {
  if (!ws || ws.readyState !== 1) return null;
  const id = `p${Date.now()}-${++_pingCounter}`;
  try {
    ws.send(JSON.stringify({ event: "ping", source: "elpm", id, at: Date.now() }));
    return id;
  } catch (e) { void e; return null; }
}

export async function generateNewCode() {
  const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bridge/session`, { method: "POST" });
  if (!r.ok) throw new Error(`Couldn't generate code (HTTP ${r.status})`);
  const data = await r.json();
  return data.session_code;
}

export function connect(code) {
  const clean = (code || "").trim().toLowerCase();
  if (!/^[a-z0-9]{4,12}$/.test(clean)) {
    throw new Error("Session code must be 4-12 lowercase alphanumeric chars.");
  }
  disconnect();
  try { localStorage.setItem(STORAGE_CODE, clean); } catch (e) { void e; }
  currentCode = clean;
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
  const url = `${base}/api/bridge/ws/${clean}`;
  ws = new WebSocket(url);
  ws.onopen = () => notify();
  ws.onclose = () => {
    ws = null;
    // iter-258 — Reset the version snapshot on disconnect so the next
    // connect doesn't accidentally show a stale "Update available"
    // pill against whoever connects next.
    lastBridgeVersion = null;
    // iter-259 — Same for the full hello payload + ping/pong state.
    lastBridgeHello = null;
    notify();
  };
  ws.onerror = () => notify();
  ws.onmessage = (evt) => {
    // iter-240/243 — inbound events from the bridge (DAW → ELPM).
    let msg = null;
    try { msg = JSON.parse(evt.data); } catch (e) { void e; return; }
    if (!msg || typeof msg !== "object") return;
    // iter-258 — bridge handshake: capture the peer's version so the
    // UI can decide whether to show an "Update available" pill.
    // iter-259 — Also fan out the full payload (ports + platform) so
    // the Bridge Health dashboard can render it.
    if (msg.event === "hello" && msg.source === "bridge" && msg.version) {
      fanoutBridgeVersion({ version: String(msg.version), at: msg.at || Date.now() });
      fanoutBridgeHello({
        version: String(msg.version),
        midi_out_port: msg.midi_out_port || null,
        midi_in_port: msg.midi_in_port || null,
        platform: msg.platform || null,
        at: msg.at || Date.now(),
      });
      return;
    }
    // iter-259 — pong: compute RTT and fan out so the health dashboard
    // can plot it. We tolerate missing `sent_at` (older bridges that
    // don't echo it) by falling back to Date.now() ↔ Date.now(), giving
    // a 0 ms baseline rather than NaN.
    if (msg.event === "pong" && msg.source === "bridge") {
      const sent = typeof msg.sent_at === "number" ? msg.sent_at : Date.now();
      const ack = typeof msg.ack_at === "number" ? msg.ack_at : Date.now();
      const rtt = Math.max(0, Date.now() - sent);
      fanoutBridgePong({ id: msg.id || null, rtt_ms: rtt, ack_at: ack });
      return;
    }
    // Only forward DAW-originated events; ignore internal room-control
    // events (joined/peer_joined/peer_left/error) so subscribers don't
    // see noise.
    const event = (msg.event || "").toString().toLowerCase();
    if (!["play", "pause", "stop", "record", "bpm", "cc", "note"].includes(event)) return;
    if (msg.source && msg.source !== "daw") return; // never re-act on our own outbound
    fanoutTransport(msg);
  };
  notify();
}

export function disconnect() {
  if (ws) {
    try { ws.close(1000, "client disconnect"); } catch (e) { void e; }
    ws = null;
  }
  currentCode = null;
  notify();
}

/** Send an event to all peers in the session (the desktop bridge). */
export function broadcast(payload) {
  if (!ws || ws.readyState !== 1) return false;
  try {
    ws.send(JSON.stringify(payload));
    return true;
  } catch (e) { void e; return false; }
}

// Convenience helpers for the transport. iter-240 — every outbound
// transport event is tagged `source:"elpm"` so the bridge can reject
// echo-back from the DAW's MIDI Out and avoid feedback loops.
// iter-243 — extended with cc() + note() helpers for the Phase 3
// extended-MIDI feature set (CC mapping + note bridging).
export const transport = {
  play:   (bpm) => broadcast({ event: "play",   bpm, source: "elpm", at: Date.now() }),
  pause:  ()    => broadcast({ event: "pause",       source: "elpm", at: Date.now() }),
  stop:   ()    => broadcast({ event: "stop",        source: "elpm", at: Date.now() }),
  record: (bpm) => broadcast({ event: "record", bpm, source: "elpm", at: Date.now() }),
  bpm:    (bpm) => broadcast({ event: "bpm",    bpm, source: "elpm", at: Date.now() }),
  /** Send a MIDI Control Change. number/value are 0-127 (clamped). channel is 0-15. */
  cc: (number, value, channel = 0) => broadcast({
    event: "cc",
    number:  Math.max(0, Math.min(127, parseInt(number, 10) || 0)),
    value:   Math.max(0, Math.min(127, parseInt(value, 10) || 0)),
    channel: Math.max(0, Math.min(15,  parseInt(channel, 10) || 0)),
    source: "elpm",
    at: Date.now(),
  }),
  /** Send a MIDI Note On (on=true) or Note Off (on=false). */
  note: (number, velocity = 100, on = true, channel = 0) => broadcast({
    event: "note",
    number:   Math.max(0, Math.min(127, parseInt(number, 10) || 0)),
    velocity: Math.max(0, Math.min(127, parseInt(velocity, 10) || 0)),
    on:       !!on,
    channel:  Math.max(0, Math.min(15,  parseInt(channel, 10) || 0)),
    source: "elpm",
    at: Date.now(),
  }),
};
